import React, { createContext, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isFileObject } from "../../utils/validation";
import { message } from "antd";
import { DocumentService } from "../../services/documentService";
import { DocCategoryService } from "../../services/docCategoryService";
import { DocCategoryDetail } from "../DocumentCategoryContext/constants";
import { DocumentAddData, DocumentUpdateData, DocumentListItem, DocumentDetail, defaultDocumentAddData, defaultDocumentUpdateData, OperationStatus } from "./constants";
import { useMemo, useCallback } from "react";
import { getErrorMessage } from "../../utils/errorUtils";

interface DocumentContextState {
    // Core Domain Data
    documentList: DocumentListItem[];
	documentForm: DocumentAddData;
	docCategoryDetail: DocCategoryDetail | null;
	documentDetail: DocumentDetail | null;
	documentUpdate: DocumentUpdateData;

    operationStatus: OperationStatus;
	failedUploadFiles: string[];

	// Setters
	setDocumentForm: React.Dispatch<React.SetStateAction<DocumentAddData>>;
	setDocumentUpdate: React.Dispatch<React.SetStateAction<DocumentUpdateData>>;
	clearDocumentData: () => void;
	clearDocumentList: () => void;
	clearFailedUploadFiles: () => void;

    // Actions
    addDocument: () => Promise<void>;
    removeDocument: (categoryId: string, docId: string) => Promise<void>;
	getDocumentList: (categoryId?: string) => Promise<void>;
	getDocumentCategoryDetail: (id: string) => Promise<void>;
	getDocumentDetail: (id: string) => Promise<void>;
	updateDocument: () => Promise<void>;
}

interface DocumentProviderProps {
    children: React.ReactNode;
}

const DocumentContext = createContext<DocumentContextState | undefined>(
	undefined
);

export const useDocumentContext = () => {
	const context = useContext(DocumentContext);
	if (!context) {
		throw new Error("useDocumentContext must be used within a DocumentProvider");
	}
	return context;
};

export const DocumentContextProvider: React.FC<DocumentProviderProps> = ({ children }) => {
	const navigate = useNavigate();
	const [documentList, setDocumentList] = useState<DocumentListItem[]>([]);
	const [documentForm, setDocumentForm] = useState<DocumentAddData>(defaultDocumentAddData);
	const [docCategoryDetail, setDocCategoryDetail] = useState<DocCategoryDetail | null>(null);
	const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(null);
	const [documentUpdate, setDocumentUpdate] = useState<DocumentUpdateData>(defaultDocumentUpdateData);
	const [operationStatus, setOperationStatus] = useState<OperationStatus>({ type: "idle" });
	const [failedUploadFiles, setFailedUploadFiles] = useState<string[]>([]);

	const parseUploadError = (errorMessage?: string, errorDetails?: unknown) => {
		if (!errorMessage) {
			return { message: "Beklenmedik Bir Hata oluştu.", failedFiles: [] as string[] };
		}

		let detailsObj: { failedFiles?: unknown } | null = null;

		if (errorDetails && typeof errorDetails === "object") {
			detailsObj = errorDetails as { failedFiles?: unknown };
		} else if (typeof errorDetails === "string") {
			try {
				const parsed = JSON.parse(errorDetails) as { failedFiles?: unknown };
				detailsObj = parsed;
			} catch {
				detailsObj = null;
			}
		}

		const failedFiles = Array.isArray(detailsObj?.failedFiles)
			? detailsObj!.failedFiles.filter((fileName): fileName is string => typeof fileName === "string" && fileName.trim().length > 0)
			: [];

		return { message: errorMessage || "Belge yükleme başarısız oldu.", failedFiles };
	};

	const isDocumentDataValid = (data: DocumentAddData) => {
		if (!data.documents || data.documents.length === 0) {
			return false;
		}

		for (const document of data.documents) {
			if (!document && !isFileObject(document)) {
				return false;
			}
		}

		return true;
	};

	const clearDocumentData = () => {
		setDocumentForm(defaultDocumentAddData);
		setDocumentUpdate(defaultDocumentUpdateData);
		setDocumentDetail(null);
		setFailedUploadFiles([]);
	};

	const clearDocumentList = () => {
		setDocumentList([]);
	};

	const clearFailedUploadFiles = () => {
		setFailedUploadFiles([]);
	};

	const addDocument = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "add" });
		setFailedUploadFiles([]);

		if (!documentForm.categoryId) {
			message.info("Doküman kategorisi bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		if (!isDocumentDataValid(documentForm)) {
			message.info("Lütfen tüm alanları doldurun.");
			setOperationStatus({ type: "idle" });
			return;
		}

		try {
			const response = await DocumentService.addDocument(documentForm);

			if (response.isSuccessful) {
				message.success("Belge başarıyla eklendi.");
				setDocumentForm(defaultDocumentAddData);
				navigate(`/documents/${docCategoryDetail?.categoryId}`);
			} else {
				const parsedError = parseUploadError(response.error?.message, response.error?.details);
				if (parsedError.failedFiles.length > 0) {
					setFailedUploadFiles(parsedError.failedFiles);
				}

				message.error(parsedError.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const axiosError = error as any;
			const serverResponse = axiosError.response?.data?.error?.message;
			const serverDetails = axiosError.response?.data?.error?.details;
			const parsedError = parseUploadError(serverResponse || (error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu."), serverDetails);
			if (parsedError.failedFiles.length > 0) {
				setFailedUploadFiles(parsedError.failedFiles);
			}

			const errMsg = parsedError.message;
			setOperationStatus({ type: "error", operation: "add", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [documentForm, docCategoryDetail, navigate]);


	const getDocumentList = useCallback(async (categoryId?: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocumentService.getDocumentList(categoryId);

			if (response.isSuccessful) {
				setDocumentList(response.result?.items || []);
			} else {
				message.error(response.error ? response.error : "Belge listesi yüklenirken hata oluştu.");
				setDocumentList([]);
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Belge listesi yüklenirken hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const removeDocument = useCallback(async (categoryId: string, docId: string) => {
		setOperationStatus({ type: "loading", operation: "delete" });

		try {
			const response = await DocumentService.removeDocument(docId);
			if (response.isSuccessful) {
				message.success("Belge başarıyla silindi.");
				await getDocumentList(categoryId);
			} else {
				message.error(response.error?.message || "Belge silinirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const axiosError = error as any;
			const serverResponse = axiosError.response?.data?.error?.message;
			const errMsg = serverResponse ?? (error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.");
			setOperationStatus({ type: "error", operation: "delete", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [getDocumentList]);

	const getDocumentCategoryDetail = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocCategoryService.getDocCategoryDetails(id);

			if (response.isSuccessful) {
				setDocCategoryDetail({
					categoryId: response.result.categoryId,
					categoryNo: response.result.categoryNo,
					categoryName: response.result.categoryName,
				});
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const getDocumentDetail = useCallback(async (id: string) => {
		setOperationStatus({ type: "loading", operation: "fetch" });

		try {
			const response = await DocumentService.getDocumentDetail(id);

			if (response.isSuccessful) {
				setDocumentDetail({
					id: response.result.id,
					categoryName: response.result.categoryName,
					displayOrder: response.result.displayOrder,
					url: response.result.url,
					name: response.result.name,
					size: response.result.size,
					mimeType: response.result.mimeType,
					extension: response.result.extension,
					creationDate: response.result.creationDate
				});
				setDocumentUpdate(prev => ({
					...prev,
					documentId: response.result.id,
				}));
			} else {
				message.error(response.error || "Belge detayı yüklenirken hata oluştu.");
			}
		} catch (error) {
			console.error(error);
			const errMsg = error instanceof Error ? error.message : "Beklenmedik Bir Hata oluştu.";
			setOperationStatus({ type: "error", operation: "fetch", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, []);

	const updateDocument = useCallback(async () => {
		setOperationStatus({ type: "loading", operation: "update" });

		if (!documentUpdate?.documentId) {
			message.info("Doküman numarası bulunamadı.");
			setOperationStatus({ type: "idle" });
			return;
		}

		const isDocumentNameChanged = documentUpdate?.documentName?.trim() && documentUpdate?.documentName?.trim() !== documentDetail?.name;
		const isDisplayOrderChanged = documentUpdate?.displayOrder && documentUpdate?.displayOrder !== documentDetail?.displayOrder;

		if (!isDocumentNameChanged && !isDisplayOrderChanged && !documentUpdate?.document) {
			message.info("Bir değişiklik yapılmadı.");
			setOperationStatus({ type: "idle" });
			return;
		}
		try {
			const response = await DocumentService.updateDocument(documentUpdate);

			if (response.isSuccessful) {
				message.success("Belge başarıyla güncellendi.");
				setDocumentUpdate(defaultDocumentUpdateData);
				navigate(`/documents/${docCategoryDetail?.categoryId}`);
			} else {
				message.error(response.error?.message || "Beklenmedik Bir Hata oluştu.");
			}
		} catch (error) {
			const errMsg = getErrorMessage(error);
			setOperationStatus({ type: "error", operation: "update", message: errMsg });
			message.error(errMsg);
		} finally {
			setOperationStatus({ type: "idle" });
		}
	}, [documentUpdate, documentDetail, docCategoryDetail, navigate]);

	const contextValue = useMemo<DocumentContextState>(
		() => ({
			documentList,
			documentForm,
			docCategoryDetail,
			documentDetail,
			documentUpdate,
			failedUploadFiles,
			setDocumentUpdate,
			setDocumentForm,
			clearDocumentData,
			clearDocumentList,
			clearFailedUploadFiles,
			operationStatus,
			addDocument,
			removeDocument,
			getDocumentCategoryDetail,
			updateDocument,
			getDocumentDetail,
			getDocumentList,
		}),
		[
			documentList,
			documentForm,
			docCategoryDetail,
			documentDetail,
			documentUpdate,
			failedUploadFiles,
			setDocumentUpdate,
			setDocumentForm,
			clearDocumentData,
			clearDocumentList,
			clearFailedUploadFiles,
			operationStatus,
			addDocument,
			removeDocument,
			getDocumentCategoryDetail,
			updateDocument,
			getDocumentDetail,
			getDocumentList,
		]
	);

	return (
		<DocumentContext.Provider value={contextValue}>
			{children}
		</DocumentContext.Provider>
	);
};
